// Lógica pura de la vista catálogo del listado de productos
// (components/producto/CatalogoProductos.tsx): precio al cliente que se enseña
// y ordenación de las tarjetas.

export type CatalogoRow = {
  id: string;
  sku: string;
  ean: string | null;
  part_number?: string | null;
  nombre: string;
  estado: string;
  estado_nombre: string | null;
  estado_color: string | null;
  accion_comercial?: string | null;
  coste: number | null;
  pvpr: number | null;
  /** Último precio al que se vendió al cliente (ERP de APPROX o pedido). */
  precio_cliente?: number | null;
  precio_cliente_fecha?: string | null;
  stock_actual: number | null;
  uds_pedidas: number | null;
  uds_pedidas_efectivas: number | null;
  /** ETA de la próxima entrada (pedido a fábrica o línea de cliente pendiente). */
  eta_proxima?: string | null;
  ventas_l4wk: number | null;
  ventas_media_3m: number | string | null;
  meses_stock: number | string | null;
  proveedor_nombre: string | null;
};

/** Hechos de la ficha que mira el popup de vista rápida para sus checks. */
export type DocumentacionHechos = {
  ean?: string | null;
  part_number?: string | null;
  gama_id?: string | null;
  categoria_id?: string | null;
  subcategoria_id?: string | null;
  proveedor_id?: string | null;
  coste?: number | string | null;
  pvpr?: number | string | null;
  revision_pendiente?: boolean | null;
  ficha_descripcion?: string | null;
  archivo_tipos?: string[] | null;
};

export type DocumentacionProducto = {
  descripcion: boolean;
  /** Datos básicos completos y sin «Revisar ficha»; `faltan` dice qué no. */
  ficha: { completa: boolean; faltan: string[] };
  manual: boolean;
  packaging: boolean;
};

const lleno = (v: unknown) => v != null && String(v).trim() !== '';

/**
 * Checks de documentación del popup de vista rápida:
 *  - Descripción: hay descripción comercial (texto, sin contar etiquetas HTML vacías).
 *  - Ficha completa: EAN, modelo, gama, categoría, subcategoría, proveedor,
 *    coste, PVPr y foto, y sin la marca «Revisar ficha».
 *  - Manual de usuario: manual PDF o User Guide subidos.
 *  - Packaging: documento de packaging subido.
 */
export function documentacionProducto(h: DocumentacionHechos): DocumentacionProducto {
  const tipos = new Set(h.archivo_tipos ?? []);
  const textoDesc = (h.ficha_descripcion ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  const requisitos: [string, boolean][] = [
    ['EAN', lleno(h.ean)],
    ['Modelo', lleno(h.part_number)],
    ['Gama', lleno(h.gama_id)],
    ['Categoría', lleno(h.categoria_id)],
    ['Subcategoría', lleno(h.subcategoria_id)],
    ['Proveedor', lleno(h.proveedor_id)],
    ['Coste', lleno(h.coste)],
    ['PVPr', lleno(h.pvpr)],
    ['Foto', tipos.has('foto')],
    ['Ficha revisada', !h.revision_pendiente],
  ];
  const faltan = requisitos.filter(([, ok]) => !ok).map(([nombre]) => nombre);
  return {
    descripcion: textoDesc !== '',
    ficha: { completa: faltan.length === 0, faltan },
    manual: tipos.has('manual_pdf') || tipos.has('user_guide'),
    packaging: tipos.has('packaging_pdf'),
  };
}

export type OrdenCatalogo = '' | 'stock_desc' | 'stock_asc' | 'pvpr_asc' | 'coste_asc';

export const ORDENES_CATALOGO: { value: OrdenCatalogo; label: string }[] = [
  { value: '', label: 'Orden del listado' },
  { value: 'stock_desc', label: 'Stock: de mayor a menor' },
  { value: 'stock_asc', label: 'Stock: de menor a mayor' },
  { value: 'pvpr_asc', label: 'PVPr: de menor a mayor' },
  { value: 'coste_asc', label: 'Coste: de menor a mayor' },
];

/** Precio al cliente que se enseña: el último vendido o, si no hay, el coste de la ficha. */
export function precioClienteDe(r: Pick<CatalogoRow, 'precio_cliente' | 'coste'>): { valor: number | null; vendido: boolean } {
  if (r.precio_cliente != null) return { valor: Number(r.precio_cliente), vendido: true };
  return { valor: r.coste != null ? Number(r.coste) : null, vendido: false };
}

/** Los que no tienen dato van al final en cualquier orden: sin stock cargado no es "el de menos stock". */
export function ordenarCatalogo<T extends Pick<CatalogoRow, 'stock_actual' | 'pvpr' | 'precio_cliente' | 'coste'>>(rows: T[], orden: OrdenCatalogo): T[] {
  if (!orden) return rows;
  const val = (r: T): number | null => {
    const v = orden.startsWith('stock') ? r.stock_actual : orden === 'pvpr_asc' ? r.pvpr : precioClienteDe(r).valor;
    const n = v == null ? NaN : Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const desc = orden === 'stock_desc';
  return [...rows].sort((a, b) => {
    const av = val(a);
    const bv = val(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return desc ? bv - av : av - bv;
  });
}
